import type { Event } from "@sitescene/types";

import { useState } from "react";
import { useNavigate } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { eventPath } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	type EventFilters,
	useEventSearch,
} from "@/features/events/use-events";
import { AutoTrackPanel } from "@/features/tracking/auto-track-panel";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { formatWhen } from "@/pages/plausibility/format";

const SEVERITIES = Array.from({ length: 10 }, (_, i) => String(i + 1));

/**
 * The selected organization's events, newest first, to find clips for the
 * gold standard set or track someone, under its auto-tracking switch. The
 * title search applies a moment after typing stops; dates are whole days in
 * the browser's time zone.
 */
export const Events = () => {
	const navigate = useNavigate();
	const { selected } = useOrgSelection();
	const [title, setTitle] = useState("");
	const [appliedTitle, setAppliedTitle] = useState("");
	const applyTitle = useDebouncedCallback(setAppliedTitle, 400);
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [severityMin, setSeverityMin] = useState("");
	const [status, setStatus] = useState("");

	const filters: EventFilters = {
		endTimestamp: to ? endOfDay(to) : undefined,
		severityMin: severityMin || undefined,
		startTimestamp: from
			? new Date(`${from}T00:00:00`).toISOString()
			: undefined,
		status: status || undefined,
		title: appliedTitle.trim() || undefined,
	};
	const query = useEventSearch(filters);
	const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

	const columns: Column<Event>[] = [
		{
			header: "Started",
			key: "start",
			render: (row) => formatWhen(row.startTimestamp),
		},
		{
			header: "Event",
			key: "title",
			render: (row) =>
				row.title || <span className="text-tertiary">Untitled</span>,
		},
		{ header: "Severity", key: "severity", render: (row) => row.severity },
		{
			header: "Status",
			key: "status",
			render: (row) => <Badge label={row.status} value={row.status} />,
		},
		{
			header: "Clips",
			key: "clips",
			render: (row) => row.mediaKeys?.length ?? 0,
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<AutoTrackPanel key={selected?.orgCode ?? ""} />
			<form
				aria-label="Filter events"
				className="flex flex-wrap items-end gap-3"
				onSubmit={(event) => event.preventDefault()}
			>
				<Input
					className="w-72 max-w-full"
					label="Title contains"
					onChange={(value) => {
						setTitle(value);
						applyTitle.schedule(value);
					}}
					placeholder="rifle, fall, gate…"
					value={title}
				/>
				<Input label="From" onChange={setFrom} type="date" value={from} />
				<Input label="To" onChange={setTo} type="date" value={to} />
				<NativeSelect
					label="Severity at least"
					onChange={(e) => setSeverityMin(e.target.value)}
					options={[
						{ label: "Any", value: "" },
						...SEVERITIES.map((value) => ({ label: value, value })),
					]}
					value={severityMin}
				/>
				<NativeSelect
					label="Status"
					onChange={(e) => setStatus(e.target.value)}
					options={[
						{ label: "Any", value: "" },
						{ label: "Open", value: "open" },
						{ label: "Resolved", value: "resolved" },
					]}
					value={status}
				/>
			</form>

			<div>
				<StatusMessage
					empty="No events match."
					error={query.error}
					isEmpty={!query.isLoading && rows.length === 0}
					isLoading={query.isLoading}
					loading="Loading events…"
				/>
				{rows.length > 0 && (
					<>
						<DataTable
							columns={columns}
							onRowClick={(row) => void navigate(eventPath(row.id))}
							rowKey={(row) => row.id}
							rows={rows}
						/>
						{query.hasNextPage && (
							<div className="mt-3">
								<Button
									color="secondary"
									isDisabled={query.isFetchingNextPage}
									onClick={() => void query.fetchNextPage()}
									size="sm"
								>
									{query.isFetchingNextPage ? "Loading…" : "Load more"}
								</Button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

/** The last moment of a `YYYY-MM-DD` day, in the browser's time zone. */
const endOfDay = (day: string): string =>
	new Date(`${day}T23:59:59.999`).toISOString();
